/* eslint-disable max-lines */
/**
 * Find & Replace popover (#2233).
 *
 * Wide, header-anchored popover that surfaces the full result list — the
 * user can review every match in context before pressing Replace all.
 * The popover is dumb chrome: state and matching live in
 * `useFindReplace`; the caller wires replace-all/replace-this back into
 * their document store.
 *
 * Every form control here reuses the shared ui-kit atoms (`Input`,
 * `Button`, `Checkbox`, `Tooltip`) so styling / focus / keyboard
 * behaviour stays consistent with the rest of the app. Native
 * `<button>` / `<input>` / `<label>` elements are only used for the
 * MATCH LIST rows (each match needs `role="option"` + arbitrary inner
 * highlight markup) and the close X, which are chrome-specific.
 */

import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import type { UseFindReplaceResult } from './use-find-replace.ts';

export interface FindReplacePopoverProps {
  /** State from `useFindReplace(...)`. */
  state: UseFindReplaceResult;
  /** Called when the user closes the popover (Escape / X). */
  onClose: () => void;
  /** Called with the current match index when a result row is clicked. */
  onJumpToMatch?: (index: number) => void;
  /** Called when the user hits "Replace all". */
  onReplaceAll: () => void;
  /**
   * Called when the user hits "Replace" (singular). Replaces only the
   * currently-highlighted match and advances to the next one. The
   * popover renders the button only when this handler is provided —
   * callers that haven't wired single-match replace yet get the
   * previous Replace-all-only layout.
   */
  onReplaceOne?: () => void;
  /** Optional trailing content next to Replace All (e.g. Replace this). */
  extraFooter?: ReactNode;
}

const RESULT_ROW_HEIGHT = 40;

export function FindReplacePopover({
  state,
  onClose,
  onJumpToMatch,
  onReplaceAll,
  onReplaceOne,
  extraFooter
}: FindReplacePopoverProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedRef = useRef<Element | null>(null);

  // Autofocus find field and remember where focus came from, so
  // closing the popover can hand focus back to the search trigger.
  useLayoutEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    inputRef.current?.focus();
    inputRef.current?.select();
    return () => {
      const prev = previouslyFocusedRef.current;
      if (prev instanceof HTMLElement) prev.focus();
    };
  }, []);

  // Global Escape → close. `capture: true` so we run before any other
  // handler swallows the key inside the editor.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () =>
      window.removeEventListener('keydown', handler, { capture: true });
  }, [onClose]);

  // Focus trap (#2233 reviewer gap). Tab from the last focusable
  // element wraps back to the first; Shift+Tab from the first wraps
  // to the last. Keeps keyboard focus inside the popover for as long
  // as it's open — Escape is still the way out. Queried fresh on each
  // keystroke so matches rendered after mount are included.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const SELECTOR =
      'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [role="option"], [tabindex]:not([tabindex="-1"])';
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(SELECTOR)
      ).filter(el => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      // Shift+Tab from the first element (or something outside the
      // popover) wraps to the last. Plain Tab from the last wraps to
      // the first. Mid-list Tab/Shift+Tab is left to the browser.
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    root.addEventListener('keydown', handler);
    return () => root.removeEventListener('keydown', handler);
  }, []);

  // Keep the active match row in view as the user pages through.
  useEffect(() => {
    const row = activeRowRef.current;
    if (!row) return;
    row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [state.currentIndex]);

  const handleRowClick = useCallback(
    (index: number) => {
      state.jumpTo(index);
      onJumpToMatch?.(index);
    },
    [state, onJumpToMatch]
  );

  const total = state.matches.length;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="false"
      aria-label={t('find-and-replace')}
      className="flex flex-col w-[420px] max-w-[92vw] max-h-full rounded-2xl border shadow-2xl overflow-hidden"
      style={{
        backgroundColor: getColour('secondLayer'),
        borderColor: getColour('border')
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ borderColor: getColour('border') }}
      >
        <h2
          className="text-sm font-semibold m-0"
          style={{ color: getColour('normalText') }}
        >
          {t('find')}
        </h2>
        <Tooltip label={t('close')}>
          <Button
            variant="smoke"
            size="sm"
            onClick={onClose}
            aria-label={t('close')}
            icon={<X size={14} aria-hidden="true" />}
          />
        </Tooltip>
      </div>

      {/* Find field */}
      <div
        className="px-3 py-2 border-b"
        style={{ borderColor: getColour('border') }}
      >
        <Input
          ref={inputRef}
          value={state.query}
          onChange={e => state.setQuery(e.target.value)}
          placeholder={t('find')}
          aria-label={t('find')}
          size="small"
        />

        {/* Options */}
        <div className="mt-2 flex flex-col gap-1.5">
          <Checkbox
            label={t('case-sensitive')}
            checked={state.options.caseSensitive}
            onChange={e => state.setOption('caseSensitive', e.target.checked)}
          />
          <Checkbox
            label={t('ignore-diacritics')}
            checked={state.options.ignoreDiacritics}
            onChange={e =>
              state.setOption('ignoreDiacritics', e.target.checked)
            }
          />
          <Checkbox
            label={t('match-full-word')}
            checked={state.options.wholeWord}
            onChange={e => state.setOption('wholeWord', e.target.checked)}
          />
        </div>
      </div>

      {/* Replace field */}
      <div
        className="px-3 py-2 border-b"
        style={{ borderColor: getColour('border') }}
      >
        <Input
          label={t('replace-with')}
          value={state.replacement}
          onChange={e => state.setReplacement(e.target.value)}
          placeholder={t('replace-with')}
          aria-label={t('replace-with')}
          size="small"
        />
        <div className="mt-2">
          <Checkbox
            label={t('preserve-capitalisation-of-first-letter')}
            checked={state.preserveFirstLetterCase}
            onChange={e => state.setPreserveFirstLetterCase(e.target.checked)}
          />
        </div>
      </div>

      {/* Result list — kept as native <button role="option"> rows
          because each row carries arbitrary highlight markup
          (mark + surrounding-context text) that the shared Button
          doesn't model.

          Grows into whatever vertical space is left under the fields
          and above the footer. The footer stays reachable because the
          dialog root is a flex column with max-h-full and only this
          region is allowed to scroll (#2329). */}
      <div
        ref={listRef}
        role="listbox"
        aria-label={t('find-and-replace-matches')}
        className="flex-1 min-h-0 overflow-y-auto"
      >
        {total === 0 ? (
          <p
            className="text-xs text-center py-6 opacity-70"
            style={{ color: getColour('inactiveText') }}
          >
            {state.query
              ? t('no-matches-found')
              : t('type-to-search-the-document')}
          </p>
        ) : (
          state.matches.map((match, index) => {
            const isActive = index === state.currentIndex;
            return (
              <button
                key={match.id}
                ref={isActive ? activeRowRef : undefined}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => handleRowClick(index)}
                className={cn(
                  'w-full text-left px-3 py-2 border-b text-xs flex items-baseline gap-1 min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                )}
                style={{
                  borderColor: getColour('hairline'),
                  backgroundColor: isActive
                    ? getColour('thirdLayer')
                    : 'transparent',
                  color: getColour('normalText'),
                  minHeight: RESULT_ROW_HEIGHT
                }}
              >
                <span className="truncate opacity-70">
                  {match.contextBefore}
                </span>
                <mark
                  className="rounded px-0.5"
                  style={{
                    backgroundColor: 'rgba(250, 204, 21, 0.35)',
                    color: getColour('normalText')
                  }}
                >
                  {match.text}
                </mark>
                <span className="truncate opacity-70">
                  {match.contextAfter}
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-2"
        style={{ backgroundColor: getColour('firstLayer') }}
      >
        <div className="flex items-center gap-2">
          {onReplaceOne && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onReplaceOne}
              disabled={total === 0 || !state.replacement}
              label={t('replace')}
            />
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={onReplaceAll}
            disabled={total === 0 || !state.replacement}
            label={t('replace-all')}
          />
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <span
            className="text-xs tabular-nums"
            style={{ color: getColour('inactiveText') }}
            aria-live="polite"
            aria-atomic="true"
          >
            {total === 0
              ? t('no-matches-found-short') || '0'
              : `${state.currentIndex + 1} / ${total}`}
          </span>
          <Tooltip label={t('previous-match')}>
            <Button
              variant="ghost"
              size="sm"
              onClick={state.prev}
              disabled={total === 0}
              aria-label={t('previous-match')}
              icon={<ChevronUp size={14} aria-hidden="true" />}
              className="h-8 w-8 p-0"
            />
          </Tooltip>
          <Tooltip label={t('next-match')}>
            <Button
              variant="ghost"
              size="sm"
              onClick={state.next}
              disabled={total === 0}
              aria-label={t('next-match')}
              icon={<ChevronDown size={14} aria-hidden="true" />}
              className="h-8 w-8 p-0"
            />
          </Tooltip>
        </div>

        {extraFooter}
      </div>
    </div>
  );
}
